import React from 'react';
import {
    CalendarDaysIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    SunIcon,
} from '@heroicons/react/24/outline';
import { daysUntilIsoDate } from './vacationEnjoymentStart';
import {
    EXPIRING_CAPTION,
    INDICATOR_CAPTION_CLASSES,
    INDICATOR_CARD_CLASSES,
    INDICATOR_ICON_CLASSES,
    INDICATOR_LABELS,
    INDICATOR_LABEL_CLASSES,
    INDICATOR_VALUE_CLASSES,
    NO_DEADLINE,
    SCHEDULED_CAPTION,
    buildIdleCaption,
    buildRemainingCaption,
    pluralDays,
    resolveDeadlineTone,
    IDLE_VALUES,
} from './vacationIndicators.definitions';
import type { IndicatorTone } from './vacationIndicators.definitions';

interface NextCompulsoryPeriod {
    days: number;
    date: string | null;
}

interface VacationIndicatorsProps {
    balance: number;
    expiredCompulsoryDays: number;
    nextCompulsoryPeriod: NextCompulsoryPeriod;
    scheduledDays: number;
    isServerCalculation?: boolean;
    deadlineVariant?: 'deadline' | 'upToDate' | 'allScheduled';
}

interface IndicatorProps {
    label: string;
    value: string | number;
    unit?: string;
    caption?: string | null;
    valueSize?: 'number' | 'date' | 'text';
    tone?: IndicatorTone;
    icon: React.ComponentType<React.ComponentProps<'svg'>>;
}

const Indicator: React.FC<IndicatorProps> = ({
    label,
    value,
    unit,
    caption,
    valueSize = 'number',
    tone = 'neutral',
    icon: Icon,
}) => (
    <div className={`flex flex-col rounded-xl border p-4 shadow-sm ${INDICATOR_CARD_CLASSES[tone]}`}>
        <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 shrink-0 ${INDICATOR_ICON_CLASSES[tone]}`} aria-hidden="true" />
            <p className={`text-sm font-medium ${INDICATOR_LABEL_CLASSES[tone]}`}>{label}</p>
        </div>

        <p className={`mt-2 flex items-baseline gap-1.5 ${INDICATOR_VALUE_CLASSES[tone]}`}>
            <span
                className={`font-semibold tabular-nums leading-none ${
                    valueSize === 'text' ? 'text-xl' : valueSize === 'date' ? 'text-2xl' : 'text-3xl'
                }`}
            >
                {value}
            </span>
            {unit && <span className="text-sm font-medium text-gray-500">{unit}</span>}
        </p>

        <p className={`mt-1.5 min-h-[1rem] text-xs ${INDICATOR_CAPTION_CLASSES[tone]}`}>
            {caption ?? ''}
        </p>
    </div>
);

const VacationIndicators: React.FC<VacationIndicatorsProps> = ({
    balance,
    expiredCompulsoryDays,
    nextCompulsoryPeriod,
    scheduledDays,
    isServerCalculation = false,
    deadlineVariant,
}) => {
    const isIdleVariant = deadlineVariant === 'upToDate' || deadlineVariant === 'allScheduled';
    const daysLeft = isIdleVariant ? null : daysUntilIsoDate(nextCompulsoryPeriod.date);
    const deadlineTone = isIdleVariant
        ? deadlineVariant === 'upToDate'
            ? 'success'
            : 'neutral'
        : resolveDeadlineTone(daysLeft);
    const hasDeadline = Boolean(nextCompulsoryPeriod.date) && !isIdleVariant;

    const deadlineLabel = isIdleVariant ? INDICATOR_LABELS.status : INDICATOR_LABELS.deadline;

    const deadlineValue = isIdleVariant
        ? IDLE_VALUES[deadlineVariant]
        : hasDeadline
            ? new Date(nextCompulsoryPeriod.date as string).toLocaleDateString('pt-BR', {
                  timeZone: 'UTC',
              })
            : NO_DEADLINE;

    const deadlineCaption = isIdleVariant
        ? buildIdleCaption(deadlineVariant, nextCompulsoryPeriod.date)
        : buildRemainingCaption(daysLeft);

    const deadlineIcon = isIdleVariant && deadlineVariant === 'upToDate' ? CheckCircleIcon : ClockIcon;

    return (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Indicator
                icon={SunIcon}
                label={INDICATOR_LABELS.balance}
                value={balance}
                unit={pluralDays(balance)}
                caption={isServerCalculation && expiredCompulsoryDays > 0 ? `${balance - expiredCompulsoryDays} no prazo · ${expiredCompulsoryDays} vencidos` : undefined}
                tone={balance > 0 ? 'neutral' : 'muted'}
            />

            <Indicator
                icon={ExclamationTriangleIcon}
                label="Dias Vencidos"
                value={expiredCompulsoryDays}
                unit={pluralDays(expiredCompulsoryDays)}
                caption={expiredCompulsoryDays > 0 ? 'limite para agendar passou' : null}
                tone={expiredCompulsoryDays > 0 ? 'danger' : 'muted'}
            />

            <Indicator
                icon={deadlineIcon}
                label={deadlineLabel}
                value={deadlineValue}
                caption={deadlineCaption}
                valueSize={isIdleVariant ? 'text' : 'date'}
                tone={deadlineTone}
            />

            <Indicator
                icon={CalendarDaysIcon}
                label={INDICATOR_LABELS.scheduled}
                value={scheduledDays}
                unit={pluralDays(scheduledDays)}
                caption={scheduledDays > 0 ? SCHEDULED_CAPTION : null}
                tone={scheduledDays > 0 ? 'neutral' : 'muted'}
            />
        </div>
    );
};

export default VacationIndicators;
